import { useState, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentTenantId } from "@/utils/tenantUtils";
import { useToast } from "@/components/ui/use-toast";

// ============================================================================
// Types
// ============================================================================

/** Field names for document categories in the system */
export type DocumentFieldName =
  | "power_bill_file"
  | "drivers_license_file"
  | "satellite_image_file"
  | "site_photos_files"
  | "deal_structure_docs_file"
  | "design_image_file"
  | "proposal_file"
  | "new_proposal_file"
  | "new_sales_agreement_file"
  | "misc_assets_files"
  | "approved_work_order_file"
  | "pto_letter_file";

/** All document field names for iteration */
export const DOCUMENT_FIELD_NAMES: DocumentFieldName[] = [
  "power_bill_file",
  "drivers_license_file",
  "satellite_image_file",
  "site_photos_files",
  "deal_structure_docs_file",
  "design_image_file",
  "proposal_file",
  "new_proposal_file",
  "new_sales_agreement_file",
  "misc_assets_files",
  "approved_work_order_file",
  "pto_letter_file",
];

/** Document metadata for preview display */
export interface DocumentMetadata {
  fileType: string;
  fileName: string;
  uploadedAt?: string;
}

/** Document record from database with URL */
export interface ProjectDocument {
  id: string;
  project_id: string;
  tenant_id: string;
  storage_path: string;
  file_name: string;
  file_type: string;
  file_size_bytes: number;
  document_type: string;
  description: string;
  created_at: string;
  updated_at: string;
  uploaded_by_user_id?: string;
  publicUrl?: string;
  isPublicUrl?: boolean;
}

/** Gallery document for image viewer */
export interface GalleryDocument {
  url: string;
  fileName: string;
  fieldName: string;
  index: number;
  uploadedAt?: string;
  isImage?: boolean;
}

/** Gallery state for image viewer */
export interface GalleryState {
  isOpen: boolean;
  documents: GalleryDocument[];
  initialIndex: number;
}

/** Driver's license verification data */
export interface DLVerificationData {
  name?: string;
  licenseNumber?: string;
  expirationDate?: string;
  state?: string;
  confidence?: number;
}

/** Utility bill verification data */
export interface UBVerificationData {
  customerName?: string;
  utilityCompany?: string;
  accountNumber?: string;
  serviceAddress?: string;
  dueDate?: string;
  invoiceDate?: string;
  amountDue?: number;
  confidence?: number;
}

export type VerificationStatus = "unverified" | "verified" | "processing";

export interface UseProjectDocumentsOptions {
  projectId: string | null;
  userId?: string;
  /** Callback after successful upload */
  onUploadSuccess?: (fieldName: string, fileName: string) => void;
  /** Callback after upload error */
  onUploadError?: (fieldName: string, error: Error) => void;
  /** Callback after successful delete */
  onDeleteSuccess?: (fieldName: string, fileName: string) => void;
  /** Callback to update form values (for satellite_image_path, etc.) */
  onFormValueUpdate?: (updates: Record<string, unknown>) => void;
}

export interface UseProjectDocumentsReturn {
  // Document state
  documents: Record<string, ProjectDocument[]>;
  filePreviewUrls: Record<string, string[]>;
  documentMetadata: Record<string, DocumentMetadata[]>;
  fileLoading: Record<string, boolean>;
  uploadStatus: Record<string, string>;
  documentUpdateTimestamp: number;

  // Document counts (for lazy loading - fast initial fetch)
  documentCounts: Record<string, number>;
  countsLoading: boolean;
  loadedFields: Set<string>;

  // Drag state
  dragActive: Record<string, boolean>;

  // Gallery state
  galleryState: GalleryState;
  setGalleryState: React.Dispatch<React.SetStateAction<GalleryState>>;

  // Verification state
  dlVerificationStatus: VerificationStatus;
  dlVerificationData: DLVerificationData | null;
  isVerifying: boolean;
  ubVerificationStatus: VerificationStatus;
  ubVerificationData: UBVerificationData | null;
  isVerifyingUb: boolean;

  // Document operations
  fetchProjectDocuments: (projectId: string) => Promise<void>;
  fetchDocumentCounts: (projectId: string) => Promise<void>;
  fetchFieldDocuments: (projectId: string, fieldName: string) => Promise<void>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleRemoveFile: (fieldName: DocumentFieldName, indexToRemove: number) => Promise<void>;

  // Drag handlers
  handleDrag: (e: React.DragEvent, field: string, active: boolean) => void;
  handleDrop: (e: React.DragEvent, field: string) => Promise<void>;

  // Verification handlers
  handleVerifyDriversLicense: (documentOverride?: ProjectDocument) => Promise<void>;
  handleVerifyUtilityBill: (documentOverride?: ProjectDocument) => Promise<void>;

  // Gallery helpers
  buildGalleryDocuments: (fieldName: string) => GalleryDocument[];
  isImageFileType: (fileType: string) => boolean;

  // Direct setters for edge cases
  setDocuments: React.Dispatch<React.SetStateAction<Record<string, ProjectDocument[]>>>;
  setFilePreviewUrls: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  setDocumentMetadata: React.Dispatch<React.SetStateAction<Record<string, DocumentMetadata[]>>>;
  setDlVerificationStatus: React.Dispatch<React.SetStateAction<VerificationStatus>>;
  setDlVerificationData: React.Dispatch<React.SetStateAction<DLVerificationData | null>>;
  setUbVerificationStatus: React.Dispatch<React.SetStateAction<VerificationStatus>>;
  setUbVerificationData: React.Dispatch<React.SetStateAction<UBVerificationData | null>>;
}

// ============================================================================
// Constants
// ============================================================================

/** Initial empty state for document records by field */
const INITIAL_DOCUMENTS: Record<string, ProjectDocument[]> = {
  power_bill_file: [],
  drivers_license_file: [],
  satellite_image_file: [],
  site_photos_files: [],
  deal_structure_docs_file: [],
  design_image_file: [],
  proposal_file: [],
  new_proposal_file: [],
  new_sales_agreement_file: [],
  misc_assets_files: [],
};

/** Initial loading state for all fields */
const INITIAL_FILE_LOADING: Record<string, boolean> = {
  power_bill_file: false,
  drivers_license_file: false,
  satellite_image_file: false,
  site_photos_files: false,
  deal_structure_docs_file: false,
  design_image_file: false,
  proposal_file: false,
  new_proposal_file: false,
  new_sales_agreement_file: false,
  misc_assets_files: false,
  approved_work_order_file: false,
  pto_letter_file: false,
};

/** Initial preview URLs state */
const INITIAL_FILE_PREVIEW_URLS: Record<string, string[]> = {
  power_bill_file: [],
  drivers_license_file: [],
  satellite_image_file: [],
  site_photos_files: [],
  deal_structure_docs_file: [],
  design_image_file: [],
  proposal_file: [],
  new_proposal_file: [],
  new_sales_agreement_file: [],
  misc_assets_files: [],
  approved_work_order_file: [],
  pto_letter_file: [],
};

/** Supported image types for browser display */
const SUPPORTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
];

// ============================================================================
// Hook Implementation
// ============================================================================

export function useProjectDocuments({
  projectId,
  userId,
  onUploadSuccess,
  onUploadError,
  onDeleteSuccess,
  onFormValueUpdate,
}: UseProjectDocumentsOptions): UseProjectDocumentsReturn {
  const { toast } = useToast();

  // Document state
  const [documents, setDocuments] = useState<Record<string, ProjectDocument[]>>(INITIAL_DOCUMENTS);
  const [filePreviewUrls, setFilePreviewUrls] = useState<Record<string, string[]>>(INITIAL_FILE_PREVIEW_URLS);
  const [documentMetadata, setDocumentMetadata] = useState<Record<string, DocumentMetadata[]>>({});
  const [fileLoading, setFileLoading] = useState<Record<string, boolean>>(INITIAL_FILE_LOADING);
  const [uploadStatus, setUploadStatus] = useState<Record<string, string>>({});
  const [documentUpdateTimestamp, setDocumentUpdateTimestamp] = useState(Date.now());

  // Lazy loading state - counts only (fast initial load)
  const [documentCounts, setDocumentCounts] = useState<Record<string, number>>({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [loadedFields, setLoadedFields] = useState<Set<string>>(new Set());

  // Drag state
  const [dragActive, setDragActive] = useState<Record<string, boolean>>({});

  // Gallery state
  const [galleryState, setGalleryState] = useState<GalleryState>({
    isOpen: false,
    documents: [],
    initialIndex: 0,
  });

  // Verification state - Driver's License
  const [dlVerificationStatus, setDlVerificationStatus] = useState<VerificationStatus>("unverified");
  const [dlVerificationData, setDlVerificationData] = useState<DLVerificationData | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Verification state - Utility Bill
  const [ubVerificationStatus, setUbVerificationStatus] = useState<VerificationStatus>("unverified");
  const [ubVerificationData, setUbVerificationData] = useState<UBVerificationData | null>(null);
  const [isVerifyingUb, setIsVerifyingUb] = useState(false);

  // Refs
  const isFetching = useRef(false);

  // ============================================================================
  // Helper Functions
  // ============================================================================

  /** Check if a file type is an image that browsers can display */
  const isImageFileType = useCallback((fileType: string): boolean => {
    return fileType ? SUPPORTED_IMAGE_TYPES.includes(fileType.toLowerCase()) : false;
  }, []);

  /** Build gallery documents for a specific field */
  const buildGalleryDocuments = useCallback((fieldName: string): GalleryDocument[] => {
    const fieldDocs: GalleryDocument[] = [];
    const urls = filePreviewUrls[fieldName] || [];
    const metadataArray = documentMetadata[fieldName] || [];

    urls.forEach((url, index) => {
      const metadata = metadataArray[index];
      const fileName = metadata?.fileName || `File ${index + 1}`;

      // Determine if this is an image
      let isImage = false;
      if (metadata && metadata.fileType) {
        isImage = isImageFileType(metadata.fileType);
      }

      // Fallback: check file extension
      if (!isImage && fileName) {
        isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName.toLowerCase());
      }

      fieldDocs.push({
        url,
        fileName,
        fieldName,
        index,
        uploadedAt: metadata?.uploadedAt,
        isImage,
      });
    });

    return fieldDocs;
  }, [filePreviewUrls, documentMetadata, isImageFileType]);

  /** Map field name to document type for database storage */
  const getDocumentTypeAndDescription = (fieldName: string, mimeType: string): { documentType: string; description: string } => {
    const fieldMappings: Record<string, { documentType: string; description: string }> = {
      power_bill_file: { documentType: "invoice", description: "Power Bill" },
      drivers_license_file: { documentType: "id", description: "Driver's License" },
      satellite_image_file: { documentType: "photo", description: "Satellite Image" },
      site_photos_files: { documentType: "photo", description: "Site Photo" },
      deal_structure_docs_file: { documentType: "contract", description: "Deal Structure Document" },
      design_image_file: { documentType: "design", description: "Design Image" },
      proposal_file: { documentType: "proposal", description: "Proposal" },
      new_proposal_file: { documentType: "proposal", description: "New Proposal" },
      new_sales_agreement_file: { documentType: "sales_agreement", description: "New Sales Agreement" },
      misc_assets_files: { documentType: "misc", description: "Misc. Image/Asset" },
    };

    if (fieldMappings[fieldName]) {
      return fieldMappings[fieldName];
    }

    // Fallback based on mime type
    if (mimeType.includes("image")) {
      return { documentType: "photo", description: "Photo" };
    } else if (mimeType.includes("pdf")) {
      return { documentType: "design", description: "Document" };
    } else if (mimeType.includes("sheet") || mimeType.includes("excel")) {
      return { documentType: "invoice", description: "Spreadsheet" };
    }

    return { documentType: "other", description: "Other" };
  };

  /** Categorize document by its type/description */
  const categorizeDocument = (doc: ProjectDocument): string | null => {
    const { description, document_type, storage_path } = doc;

    if (description === "Power Bill" || document_type === "bill" || document_type === "invoice" || storage_path.includes("power_bill_file")) {
      return "power_bill_file";
    }
    if (description === "Driver's License" || document_type === "id") {
      return "drivers_license_file";
    }
    if (description === "Satellite Image" || (document_type === "photo" && description === "Satellite Image")) {
      return "satellite_image_file";
    }
    if (description === "Site Photo" || (document_type === "photo" && description !== "Satellite Image" && description !== "Design Image")) {
      return "site_photos_files";
    }
    if (description === "Deal Structure Document" || document_type === "contract") {
      return "deal_structure_docs_file";
    }
    if (description === "Design Image" || (document_type === "design" && description === "Design Image")) {
      return "design_image_file";
    }
    if (description === "Proposal" || (document_type === "proposal" && description !== "New Proposal")) {
      return "proposal_file";
    }
    if (description === "New Proposal" || (document_type === "proposal" && description === "New Proposal")) {
      return "new_proposal_file";
    }
    if (description === "New Sales Agreement" || document_type === "sales_agreement") {
      return "new_sales_agreement_file";
    }
    if (description === "Misc. Image/Asset" || description === "Other Document" || document_type === "misc" || document_type === "other") {
      return "misc_assets_files";
    }

    return null;
  };

  // ============================================================================
  // Fetch Document Counts (Fast - for lazy loading)
  // ============================================================================

  /**
   * Fetches only document counts per field - no signed URLs or file data.
   * This is much faster than fetchProjectDocuments and should be used for initial load.
   */
  const fetchDocumentCounts = useCallback(async (projId: string) => {
    if (!projId) return;

    try {
      setCountsLoading(true);

      const tenantId = await getCurrentTenantId();

      // Fetch all documents for this project (just metadata, no URLs)
      const { data: docs, error } = await supabase
        .from("documents")
        .select("id, description, document_type, storage_path")
        .eq("project_id", projId)
        .eq("tenant_id", tenantId);

      if (error) {
        console.error("Error fetching document counts:", error);
        return;
      }

      // Count documents by category
      const counts: Record<string, number> = {
        power_bill_file: 0,
        drivers_license_file: 0,
        satellite_image_file: 0,
        site_photos_files: 0,
        deal_structure_docs_file: 0,
        design_image_file: 0,
        proposal_file: 0,
        new_proposal_file: 0,
        new_sales_agreement_file: 0,
        misc_assets_files: 0,
      };

      if (docs) {
        for (const doc of docs) {
          const category = categorizeDocument(doc as ProjectDocument);
          if (category && counts[category] !== undefined) {
            counts[category]++;
          }
        }
      }

      setDocumentCounts(counts);
    } catch (err) {
      console.error("Error in fetchDocumentCounts:", err);
    } finally {
      setCountsLoading(false);
    }
  }, []);

  // ============================================================================
  // Fetch Field Documents (On-demand - for lazy loading)
  // ============================================================================

  /**
   * Fetches documents for a specific field only, including signed URLs.
   * Called when user expands a document section accordion.
   */
  const fetchFieldDocuments = useCallback(async (projId: string, fieldName: string) => {
    if (!projId || !fieldName) return;

    // Skip if already loaded
    if (loadedFields.has(fieldName)) return;

    try {
      // Set loading state for this field only
      setFileLoading((prev) => ({ ...prev, [fieldName]: true }));

      const tenantId = await getCurrentTenantId();

      // Fetch all documents for this project
      const { data: docs, error } = await supabase
        .from("documents")
        .select("*")
        .eq("project_id", projId)
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error(`Error fetching documents for ${fieldName}:`, error);
        setFileLoading((prev) => ({ ...prev, [fieldName]: false }));
        return;
      }

      if (!docs || docs.length === 0) {
        setFileLoading((prev) => ({ ...prev, [fieldName]: false }));
        setLoadedFields((prev) => new Set(prev).add(fieldName));
        return;
      }

      // Filter to only this field's documents
      const fieldDocs: ProjectDocument[] = [];
      for (const doc of docs) {
        const category = categorizeDocument(doc as ProjectDocument);
        if (category === fieldName) {
          // Get signed URL for the file
          const { data: downloadData, error: downloadError } = await supabase.storage
            .from("project-documents")
            .createSignedUrl(doc.storage_path, 3600); // 1 hour expiry

          let documentWithUrl: ProjectDocument;

          if (downloadError) {
            console.error("Error creating signed URL:", downloadError);
            // Fallback to public URL
            const { data: publicUrlData } = await supabase.storage
              .from("project-documents")
              .getPublicUrl(doc.storage_path, { download: true });

            documentWithUrl = {
              ...doc,
              publicUrl: publicUrlData.publicUrl,
              isPublicUrl: true,
            };
          } else {
            documentWithUrl = {
              ...doc,
              publicUrl: downloadData.signedUrl,
              isPublicUrl: false,
            };
          }

          fieldDocs.push(documentWithUrl);
        }
      }

      // Update documents state for this field
      setDocuments((prev) => ({
        ...prev,
        [fieldName]: fieldDocs,
      }));

      // Create preview URLs for the UI
      if (fieldDocs.length > 0) {
        const newPreviewUrls = fieldDocs
          .map((doc) => {
            try {
              const timestamp = new Date().getTime();
              const url = doc.publicUrl
                ? `${doc.publicUrl}${doc.publicUrl.includes("?") ? "&" : "?"}t=${timestamp}`
                : "";
              return url;
            } catch (urlError) {
              console.error("Error creating preview URL:", urlError);
              return "";
            }
          })
          .filter((url) => url);

        // Store document metadata for preview logic
        const newMetadata = fieldDocs.map((doc) => ({
          fileType: doc.file_type,
          fileName: doc.file_name || doc.storage_path.split("/").pop() || "",
          uploadedAt: doc.created_at,
        }));

        setFilePreviewUrls((prev) => ({
          ...prev,
          [fieldName]: newPreviewUrls,
        }));

        setDocumentMetadata((prev) => ({
          ...prev,
          [fieldName]: newMetadata,
        }));
      }

      // Check for verification data if this is a verifiable field
      if (fieldName === "drivers_license_file" && fieldDocs.length > 0) {
        const dlDoc = fieldDocs[0];
        const { data: extraction, error: extractionError } = await supabase
          .from("document_extractions")
          .select("is_legitimate, verification_status, dl_full_name, dl_license_number, dl_expiration_date, dl_state, classification_confidence")
          .eq("document_id", dlDoc.id)
          .eq("project_id", projId)
          .eq("tenant_id", tenantId)
          .eq("classified_as", "Drivers License")
          .maybeSingle();

        if (!extractionError && extraction) {
          const isVerified = extraction.is_legitimate && extraction.verification_status === "completed";
          setDlVerificationStatus(isVerified ? "verified" : "unverified");
          if (isVerified) {
            setDlVerificationData({
              name: extraction.dl_full_name,
              licenseNumber: extraction.dl_license_number,
              expirationDate: extraction.dl_expiration_date,
              state: extraction.dl_state,
              confidence: extraction.classification_confidence > 1
                ? Math.round(extraction.classification_confidence)
                : Math.round((extraction.classification_confidence || 0) * 100),
            });
          }
        }
      }

      if (fieldName === "power_bill_file" && fieldDocs.length > 0) {
        const ubDoc = fieldDocs[0];
        const { data: ubExtraction, error: ubExtractionError } = await supabase
          .from("document_extractions")
          .select("classification_confidence, ub_customer_name, ub_account_number, ub_utility_company, ub_service_address, ub_due_date, ub_invoice_date, ub_amount_due")
          .eq("document_id", ubDoc.id)
          .eq("project_id", projId)
          .eq("tenant_id", tenantId)
          .eq("classified_as", "Utility Bill")
          .maybeSingle();

        if (!ubExtractionError && ubExtraction) {
          const confidenceThreshold = 0.7;
          const meetsConfidenceThreshold = ubExtraction.classification_confidence >= confidenceThreshold;
          const isVerified = meetsConfidenceThreshold &&
            (ubExtraction.ub_customer_name || ubExtraction.ub_account_number) &&
            (ubExtraction.ub_utility_company || ubExtraction.ub_service_address);

          setUbVerificationStatus(isVerified ? "verified" : "unverified");
          if (isVerified) {
            setUbVerificationData({
              customerName: ubExtraction.ub_customer_name,
              utilityCompany: ubExtraction.ub_utility_company,
              accountNumber: ubExtraction.ub_account_number,
              serviceAddress: ubExtraction.ub_service_address,
              dueDate: ubExtraction.ub_due_date,
              invoiceDate: ubExtraction.ub_invoice_date,
              amountDue: ubExtraction.ub_amount_due,
              confidence: ubExtraction.classification_confidence > 1
                ? Math.round(ubExtraction.classification_confidence)
                : Math.round((ubExtraction.classification_confidence || 0) * 100),
            });
          }
        }
      }

      // Mark field as loaded
      setLoadedFields((prev) => new Set(prev).add(fieldName));
      setDocumentUpdateTimestamp(Date.now());
    } catch (err) {
      console.error(`Error in fetchFieldDocuments for ${fieldName}:`, err);
    } finally {
      setFileLoading((prev) => ({ ...prev, [fieldName]: false }));
    }
  }, [loadedFields]);

  // ============================================================================
  // Fetch Documents (Full - legacy, use fetchDocumentCounts + fetchFieldDocuments instead)
  // ============================================================================

  const fetchProjectDocuments = useCallback(async (projId: string) => {
    if (!projId || isFetching.current) return;

    isFetching.current = true;

    try {
      // Set loading state for all file types
      setFileLoading((prev) =>
        Object.fromEntries(Object.keys(prev).map((key) => [key, true]))
      );

      // Ensure we have a fresh session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        console.error("No active session found when fetching documents");
        setFileLoading((prev) =>
          Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
        );
        return;
      }

      // Get all files in storage for this project
      // Note: Supabase storage.list() has a default limit of 100, so we need to paginate
      const existingFiles = new Map<string, boolean>();
      let offset = 0;
      const pageSize = 1000; // Max allowed by Supabase
      let hasMore = true;

      while (hasMore) {
        const { data: storageFiles, error: storageError } = await supabase.storage
          .from("project-documents")
          .list(projId, { limit: pageSize, offset });

        if (storageError) {
          console.error("Error listing storage files:", storageError);
          setFileLoading((prev) =>
            Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
          );
          return;
        }

        // Add files to our map
        storageFiles?.forEach((file) => {
          existingFiles.set(file.name, true);
        });

        // Check if we need to fetch more
        if (!storageFiles || storageFiles.length < pageSize) {
          hasMore = false;
        } else {
          offset += pageSize;
        }
      }

      // Fetch all documents for this project
      const tenantId = await getCurrentTenantId();
      const { data: docs, error } = await supabase
        .from("documents")
        .select("*")
        .eq("project_id", projId)
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching project documents:", error);
        setFileLoading((prev) =>
          Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
        );
        return;
      }

      if (!docs || docs.length === 0) {
        // Reset all document states
        setDocuments(INITIAL_DOCUMENTS);
        setFilePreviewUrls({});
        setDocumentMetadata({});
        setFileLoading((prev) =>
          Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
        );
        return;
      }

      // Group documents by type
      const groupedDocuments: Record<string, ProjectDocument[]> = {
        power_bill_file: [],
        drivers_license_file: [],
        satellite_image_file: [],
        site_photos_files: [],
        deal_structure_docs_file: [],
        design_image_file: [],
        proposal_file: [],
        new_proposal_file: [],
        new_sales_agreement_file: [],
        misc_assets_files: [],
      };

      // Process each document
      for (const doc of docs) {
        try {
          const fileName = doc.storage_path.split("/").pop();
          if (!fileName) {
            console.error("Invalid storage path:", doc.storage_path);
            continue;
          }

          // Check if file exists in storage list
          // Note: We no longer auto-delete "orphaned" records as this was causing data loss
          // when storage.list() didn't return all files. The file might still exist.
          if (!existingFiles.has(fileName)) {
            console.warn(
              `Document ${doc.id} references file "${fileName}" not found in storage list. ` +
              `This may be a sync issue - skipping but not deleting.`
            );
            // Try to get the file anyway - it might exist but not be in our paginated list
            // This handles edge cases with very large file counts or timing issues
          }

          // Get signed URL for the file
          const { data: downloadData, error: downloadError } = await supabase.storage
            .from("project-documents")
            .createSignedUrl(doc.storage_path, 3600); // 1 hour expiry

          let documentWithUrl: ProjectDocument;

          if (downloadError) {
            console.error("Error creating signed URL:", downloadError);
            // Fallback to public URL
            const { data: publicUrlData } = await supabase.storage
              .from("project-documents")
              .getPublicUrl(doc.storage_path, { download: true });

            documentWithUrl = {
              ...doc,
              publicUrl: publicUrlData.publicUrl,
              isPublicUrl: true,
            };
          } else {
            documentWithUrl = {
              ...doc,
              publicUrl: downloadData.signedUrl,
              isPublicUrl: false,
            };
          }

          // Categorize the document
          const category = categorizeDocument(documentWithUrl);
          if (category && groupedDocuments[category]) {
            groupedDocuments[category].push(documentWithUrl);
          }
        } catch (docError) {
          console.error("Error processing document:", docError, "Document:", doc.storage_path);
          continue;
        }
      }

      // Update documents state
      setDocuments(groupedDocuments);

      // Check for existing driver's license verification
      if (groupedDocuments.drivers_license_file.length > 0) {
        const dlDoc = groupedDocuments.drivers_license_file[0];
        const dlTenantId = await getCurrentTenantId();

        const { data: extraction, error: extractionError } = await supabase
          .from("document_extractions")
          .select("is_legitimate, verification_status, dl_full_name, dl_license_number, dl_expiration_date, dl_state, classification_confidence")
          .eq("document_id", dlDoc.id)
          .eq("project_id", projId)
          .eq("tenant_id", dlTenantId)
          .eq("classified_as", "Drivers License")
          .maybeSingle();

        if (extractionError && extractionError.code !== "PGRST116") {
          console.error("Error fetching document extraction:", extractionError);
        }

        if (!extractionError && extraction) {
          const isVerified = extraction.is_legitimate && extraction.verification_status === "completed";
          setDlVerificationStatus(isVerified ? "verified" : "unverified");
          if (isVerified) {
            setDlVerificationData({
              name: extraction.dl_full_name,
              licenseNumber: extraction.dl_license_number,
              expirationDate: extraction.dl_expiration_date,
              state: extraction.dl_state,
              confidence: extraction.classification_confidence > 1
                ? Math.round(extraction.classification_confidence)
                : Math.round((extraction.classification_confidence || 0) * 100),
            });
          }
        }
      }

      // Check for existing utility bill verification
      if (groupedDocuments.power_bill_file.length > 0) {
        const ubDoc = groupedDocuments.power_bill_file[0];
        const ubTenantId = await getCurrentTenantId();

        const { data: ubExtraction, error: ubExtractionError } = await supabase
          .from("document_extractions")
          .select("classification_confidence, ub_customer_name, ub_account_number, ub_utility_company, ub_service_address, ub_due_date, ub_invoice_date, ub_amount_due")
          .eq("document_id", ubDoc.id)
          .eq("project_id", projId)
          .eq("tenant_id", ubTenantId)
          .eq("classified_as", "Utility Bill")
          .maybeSingle();

        if (ubExtractionError && ubExtractionError.code !== "PGRST116") {
          console.error("Error fetching utility bill extraction:", ubExtractionError);
        }

        if (!ubExtractionError && ubExtraction) {
          const confidenceThreshold = 0.7;
          const meetsConfidenceThreshold = ubExtraction.classification_confidence >= confidenceThreshold;
          const isVerified = meetsConfidenceThreshold &&
            (ubExtraction.ub_customer_name || ubExtraction.ub_account_number) &&
            (ubExtraction.ub_utility_company || ubExtraction.ub_service_address);

          setUbVerificationStatus(isVerified ? "verified" : "unverified");
          if (isVerified) {
            setUbVerificationData({
              customerName: ubExtraction.ub_customer_name,
              utilityCompany: ubExtraction.ub_utility_company,
              accountNumber: ubExtraction.ub_account_number,
              serviceAddress: ubExtraction.ub_service_address,
              dueDate: ubExtraction.ub_due_date,
              invoiceDate: ubExtraction.ub_invoice_date,
              amountDue: ubExtraction.ub_amount_due,
              confidence: ubExtraction.classification_confidence > 1
                ? Math.round(ubExtraction.classification_confidence)
                : Math.round((ubExtraction.classification_confidence || 0) * 100),
            });
          }
        }
      }

      // Create preview URLs for the UI
      const newPreviewUrls: Record<string, string[]> = {};
      const newDocumentMetadata: Record<string, DocumentMetadata[]> = {};

      for (const [key, docList] of Object.entries(groupedDocuments)) {
        if (docList.length > 0) {
          newPreviewUrls[key] = docList
            .map((doc) => {
              try {
                const timestamp = new Date().getTime();
                const url = doc.publicUrl
                  ? `${doc.publicUrl}${doc.publicUrl.includes("?") ? "&" : "?"}t=${timestamp}`
                  : "";
                return url;
              } catch (urlError) {
                console.error("Error creating preview URL:", urlError);
                return "";
              }
            })
            .filter((url) => url);

          // Store document metadata for preview logic
          newDocumentMetadata[key] = docList.map((doc) => ({
            fileType: doc.file_type,
            fileName: doc.file_name || doc.storage_path.split("/").pop() || "",
            uploadedAt: doc.created_at,
          }));
        }
      }

      // Update file preview URLs
      setFilePreviewUrls((prev) => ({
        ...prev,
        ...newPreviewUrls,
      }));

      // Update document metadata
      setDocumentMetadata((prev) => ({
        ...prev,
        ...newDocumentMetadata,
      }));

      // Update timestamp
      setDocumentUpdateTimestamp(Date.now());
    } catch (err) {
      console.error("Error in fetchProjectDocuments:", err);
    } finally {
      // Always reset loading states when done
      setFileLoading((prev) =>
        Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
      );
      isFetching.current = false;
    }
  }, []);

  // ============================================================================
  // Upload Handler
  // ============================================================================

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (!files || files.length === 0 || !projectId) {
      return;
    }

    // Create preview URL for the file
    const previewUrl = URL.createObjectURL(files[0]);

    // Add to UI previews
    setFilePreviewUrls((prev) => ({
      ...prev,
      [name]: [...(prev[name] || []), previewUrl],
    }));

    // Show as uploading
    setUploadStatus((prev) => ({
      ...prev,
      [name]: "uploading",
    }));

    try {
      // Upload file to Supabase Storage
      const file = files[0];
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const storagePath = `${projectId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("project-documents")
        .upload(storagePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        setUploadStatus((prev) => ({
          ...prev,
          [name]: "error",
        }));
        toast({
          title: "Upload Failed",
          description: `Failed to upload ${file.name}. ${uploadError.message}`,
          variant: "destructive",
        });
        onUploadError?.(name, new Error(uploadError.message));
        return;
      }

      // Get document type and description based on field name
      const { documentType, description } = getDocumentTypeAndDescription(name, file.type);

      // Add document record to database
      const tenantId = await getCurrentTenantId();
      const { data: insertedDoc, error: docError } = await supabase
        .from("documents")
        .insert([
          {
            project_id: projectId,
            uploaded_by_user_id: userId,
            storage_path: storagePath,
            file_name: file.name,
            file_type: file.type,
            file_size_bytes: file.size,
            document_type: documentType,
            description: description,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            tenant_id: tenantId,
          },
        ])
        .select()
        .single();

      if (docError) {
        setUploadStatus((prev) => ({
          ...prev,
          [name]: "error",
        }));
        toast({
          title: "Failed to Save Document",
          description: `The file was uploaded but couldn't be saved to the database. ${docError.message}`,
          variant: "destructive",
        });
        onUploadError?.(name, new Error(docError.message));
        return;
      }

      // Special case for satellite image which has a column in the projects table
      if (name === "satellite_image_file") {
        const { error: updateError } = await supabase
          .from("projects")
          .update({
            satellite_image_path: storagePath,
            last_updated_by_user: userId || null,
          })
          .eq("id", projectId);

        if (updateError) {
          toast({
            title: "Warning",
            description: "Satellite image uploaded but project record couldn't be updated automatically.",
            variant: "destructive",
          });
        }

        // Notify parent to update form values
        onFormValueUpdate?.({
          satellite_image_path: storagePath,
        });
      }

      // Log the file upload to activity log
      await supabase.from("activity_logs").insert({
        project_id: projectId,
        user_id: userId,
        action_type: "document_uploaded",
        details: { file: file.name, field: name },
        timestamp: new Date().toISOString(),
        related_entity_type: "document",
        tenant_id: tenantId,
      });

      // Set success status
      setUploadStatus((prev) => ({
        ...prev,
        [name]: "success",
      }));

      // Get the public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from("project-documents")
        .getPublicUrl(storagePath);

      // Update filePreviewUrls with the actual storage URL instead of blob URL
      setFilePreviewUrls((prev) => ({
        ...prev,
        [name]: prev[name]?.map((url, index) =>
          index === prev[name].length - 1 ? publicUrl : url
        ) || [publicUrl],
      }));

      // Update document metadata with the uploaded file info
      setDocumentMetadata((prev) => ({
        ...prev,
        [name]: [
          ...(prev[name] || []),
          {
            fileType: file.type,
            fileName: file.name,
            uploadedAt: new Date().toISOString(),
          },
        ],
      }));

      toast({
        title: "Upload Success",
        description: `${file.name} uploaded successfully.`,
      });

      // Callback
      onUploadSuccess?.(name, file.name);

      // Trigger automatic verification for driver's license and utility bill
      if (insertedDoc && (name === "drivers_license_file" || name === "power_bill_file")) {
        const existingDocs = name === "drivers_license_file"
          ? documents.drivers_license_file
          : documents.power_bill_file;

        const isNewDocument = !existingDocs || existingDocs.length === 0;

        if (isNewDocument) {
          // Create a proper document object with the inserted data
          const fullDocument: ProjectDocument = {
            ...insertedDoc,
            storage_path: storagePath,
            publicUrl: previewUrl,
          };

          // Update documents state immediately with the new document
          setDocuments((prev) => ({
            ...prev,
            [name]: [fullDocument],
          }));

          // Trigger verification with a delay to ensure DB is ready
          setTimeout(async () => {
            try {
              if (name === "drivers_license_file") {
                await handleVerifyDriversLicense(fullDocument);
              } else if (name === "power_bill_file") {
                await handleVerifyUtilityBill(fullDocument);
              }

              // Reload documents after verification
              setTimeout(() => {
                if (projectId) {
                  fetchProjectDocuments(projectId);
                }
              }, 2000);
            } catch (verifyError) {
              console.error("Auto-verification failed:", verifyError);
            }
          }, 2000);
        }
      }
    } catch (error) {
      setUploadStatus((prev) => ({
        ...prev,
        [name]: "error",
      }));
      toast({
        title: "Upload Failed",
        description: `Failed to upload ${name.replace("_", " ")}. Please try again.`,
        variant: "destructive",
      });
      onUploadError?.(name, error as Error);
    }
    // Note: handleVerifyDriversLicense and handleVerifyUtilityBill are intentionally excluded
    // from dependencies as they are stable callbacks called conditionally within this callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, userId, documents, toast, onUploadSuccess, onUploadError, onFormValueUpdate, fetchProjectDocuments]);

  // ============================================================================
  // Remove Handler
  // ============================================================================

  const handleRemoveFile = useCallback(async (
    fieldName: DocumentFieldName,
    indexToRemove: number
  ) => {
    if (!projectId) return;

    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      const fileToDelete = filePreviewUrls[fieldName]?.[indexToRemove];
      if (!fileToDelete) return;

      // If it's a URL (already uploaded file), delete from storage and database
      if (fileToDelete.startsWith("http")) {
        // Extract the file path from the URL
        const urlParts = fileToDelete.split("/");
        const fileNameWithToken = urlParts[urlParts.length - 1];
        const fileName = fileNameWithToken.split("?")[0]; // Remove any query parameters
        const storagePath = `${projectId}/${fileName}`;

        // Delete from storage
        const { error: deleteError } = await supabase.storage
          .from("project-documents")
          .remove([storagePath]);

        if (deleteError) {
          console.error("Error deleting file from storage:", deleteError);
          throw deleteError;
        }

        // Delete from database
        const tenantId = await getCurrentTenantId();
        const { error: dbError } = await supabase
          .from("documents")
          .delete()
          .eq("project_id", projectId)
          .eq("tenant_id", tenantId)
          .eq("storage_path", storagePath);

        if (dbError) {
          console.error("Error deleting file from database:", dbError);
          throw dbError;
        }

        // Log the file deletion to activity log
        await supabase.from("activity_logs").insert({
          project_id: projectId,
          user_id: userId,
          action_type: "document_deleted",
          details: { file: fileName, field: fieldName },
          timestamp: new Date().toISOString(),
          related_entity_type: "document",
          tenant_id: tenantId,
        });

        // Callback
        onDeleteSuccess?.(fieldName, fileName);
      }

      // Update the file preview URLs
      setFilePreviewUrls((prev) => ({
        ...prev,
        [fieldName]: prev[fieldName]?.filter((_, index) => index !== indexToRemove) || [],
      }));

      // Update document metadata to remove the deleted file's metadata
      setDocumentMetadata((prev) => ({
        ...prev,
        [fieldName]: prev[fieldName]?.filter((_, index) => index !== indexToRemove) || [],
      }));

      // Update the documents state
      setDocuments((prev) => {
        const newDocs = { ...prev };
        if (newDocs[fieldName]) {
          newDocs[fieldName] = newDocs[fieldName].filter((_, index) => index !== indexToRemove);
        }
        return newDocs;
      });
    } catch (error) {
      console.error("Error removing file:", error);
      toast({
        title: "Error",
        description: "Failed to remove file",
        variant: "destructive",
      });
    }
  }, [projectId, userId, filePreviewUrls, toast, onDeleteSuccess]);

  // ============================================================================
  // Drag Handlers
  // ============================================================================

  const handleDrag = useCallback((e: React.DragEvent, field: string, active: boolean) => {
    e.preventDefault();
    setDragActive((prev) => ({ ...prev, [field]: active }));
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, field: string) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      // Create a synthetic event to reuse handleFileChange
      const syntheticEvent = {
        target: {
          name: field,
          files: files,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      await handleFileChange(syntheticEvent);
    }

    // Reset drag state
    setDragActive((prev) => ({ ...prev, [field]: false }));
  }, [handleFileChange]);

  // ============================================================================
  // Verification Handlers
  // ============================================================================

  const handleVerifyDriversLicense = useCallback(async (documentOverride?: ProjectDocument) => {
    const dlDocument = documentOverride || documents.drivers_license_file?.[0];
    if (!dlDocument || !dlDocument.storage_path) {
      if (filePreviewUrls.drivers_license_file?.length > 0) {
        toast({
          title: "Please Wait",
          description: "Driver's license is still uploading. Try again in a moment.",
          variant: "default",
        });
      } else {
        toast({
          title: "No Driver's License Found",
          description: "Please upload a driver's license before verifying.",
          variant: "destructive",
        });
      }
      return;
    }

    setIsVerifying(true);
    setDlVerificationStatus("processing");

    try {
      const filePath = dlDocument.storage_path;

      // Call the edge function which will handle file download and n8n webhook
      const { data, error } = await supabase.functions.invoke("verify-document-n8n", {
        body: {
          projectId: projectId,
          filePath: filePath,
          documentId: dlDocument.id,
        },
      });

      if (error) {
        throw new Error(`Verification failed: ${error.message}`);
      }

      // Process edge function response
      if (data && data.success && data.extractedData?.entities?.length > 0) {
        const entities = data.extractedData.entities;
        const extractedInfo: DLVerificationData = {};

        // Map entity types to our fields
        entities.forEach((entity: { type: string; value: string; confidence?: number }) => {
          const type = entity.type.toLowerCase();
          if (type.includes("person") || type.includes("name")) {
            extractedInfo.name = entity.value;
          } else if (type.includes("license_number") || type.includes("document_id")) {
            extractedInfo.licenseNumber = entity.value;
          } else if (type.includes("expir")) {
            extractedInfo.expirationDate = entity.value;
          } else if (type.includes("state")) {
            extractedInfo.state = entity.value;
          }
        });

        // Calculate average confidence
        const avgConfidence = entities.reduce((sum: number, e: { confidence?: number }) => sum + (e.confidence || 0), 0) / entities.length;
        const confidencePercent = avgConfidence > 1 ? Math.round(avgConfidence) : Math.round(avgConfidence * 100);

        // Determine if verified (high confidence and has key fields)
        const confidenceThreshold = avgConfidence > 1 ? 95 : 0.95;
        const isVerified = avgConfidence > confidenceThreshold && (extractedInfo.name || extractedInfo.licenseNumber);

        if (isVerified) {
          setDlVerificationStatus("verified");
          setDlVerificationData({
            ...extractedInfo,
            confidence: confidencePercent,
          });

          toast({
            title: "Verification Successful",
            description: `Driver's license verified with ${confidencePercent}% confidence.`,
          });
        } else {
          setDlVerificationStatus("unverified");
          toast({
            title: "Verification Failed",
            description: "Could not verify the driver's license. Please ensure the image is clear and valid.",
            variant: "destructive",
          });
        }
      } else {
        setDlVerificationStatus("unverified");
        toast({
          title: "Verification Failed",
          description: data?.message || "Could not process the document. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Verification error:", error);
      setDlVerificationStatus("unverified");
      toast({
        title: "Verification Error",
        description: "An error occurred while verifying the driver's license.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  }, [projectId, documents, filePreviewUrls, toast]);

  const handleVerifyUtilityBill = useCallback(async (documentOverride?: ProjectDocument) => {
    const ubDocument = documentOverride || documents.power_bill_file?.[0];
    if (!ubDocument || !ubDocument.storage_path) {
      if (filePreviewUrls.power_bill_file?.length > 0) {
        toast({
          title: "Please Wait",
          description: "Utility bill is still uploading. Try again in a moment.",
          variant: "default",
        });
      } else {
        toast({
          title: "No Utility Bill Found",
          description: "Please upload a utility bill before verifying.",
          variant: "destructive",
        });
      }
      return;
    }

    setIsVerifyingUb(true);
    setUbVerificationStatus("processing");

    try {
      const filePath = ubDocument.storage_path;

      // Call the edge function
      const { data, error } = await supabase.functions.invoke("verify-utility-bill-n8n", {
        body: {
          projectId: projectId,
          filePath: filePath,
          documentId: ubDocument.id,
        },
      });

      if (error) {
        throw new Error(`Verification failed: ${error.message}`);
      }

      // Process edge function response
      if (data && data.success && data.extractedData?.entities?.length > 0) {
        const entities = data.extractedData.entities;
        const extractedInfo: UBVerificationData = {};

        // Map entity types to our fields
        entities.forEach((entity: { type: string; value: string; confidence?: number }) => {
          const type = entity.type.toLowerCase();
          const value = entity.value;

          if (type === "receiver_name" && !extractedInfo.customerName) {
            extractedInfo.customerName = value;
          } else if (type === "supplier_name" && !extractedInfo.utilityCompany) {
            if (!extractedInfo.customerName) {
              extractedInfo.customerName = value;
            }
          } else if (type === "supplier_account_number" || type === "invoice_id") {
            extractedInfo.accountNumber = value;
          } else if (type === "supplier_address") {
            const addressLines = value.split("\n");
            if (addressLines[0] && addressLines[0].includes("PG&E")) {
              extractedInfo.utilityCompany = "PG&E";
            }
            if (!extractedInfo.serviceAddress) {
              extractedInfo.serviceAddress = value;
            }
          } else if (type === "due_date") {
            extractedInfo.dueDate = value;
          } else if (type === "invoice_date") {
            extractedInfo.invoiceDate = value;
          } else if (type === "amount_due") {
            const numValue = parseFloat(value.replace(/[$,]/g, ""));
            if (!isNaN(numValue)) {
              extractedInfo.amountDue = numValue;
            }
          }
        });

        // Calculate average confidence
        const avgConfidence = entities.reduce((sum: number, e: { confidence?: number }) => sum + (e.confidence || 0), 0) / entities.length;
        const confidencePercent = avgConfidence > 1 ? Math.round(avgConfidence) : Math.round(avgConfidence * 100);

        // Determine if verified
        const confidenceThreshold = avgConfidence > 1 ? 70 : 0.7;
        const meetsConfidence = avgConfidence >= confidenceThreshold;
        const hasRequiredFields = (extractedInfo.customerName || extractedInfo.accountNumber) &&
          (extractedInfo.utilityCompany || extractedInfo.serviceAddress);
        const isVerified = meetsConfidence && hasRequiredFields;

        if (isVerified) {
          setUbVerificationStatus("verified");
          setUbVerificationData({
            ...extractedInfo,
            confidence: confidencePercent,
          });

          toast({
            title: "Verification Successful",
            description: `Utility bill verified with ${confidencePercent}% confidence.`,
          });
        } else {
          setUbVerificationStatus("unverified");
          toast({
            title: "Verification Failed",
            description: "Could not verify the utility bill. Please ensure the document is clear and valid.",
            variant: "destructive",
          });
        }
      } else {
        setUbVerificationStatus("unverified");
        toast({
          title: "Verification Failed",
          description: data?.message || "Could not process the document. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Verification error:", error);
      setUbVerificationStatus("unverified");
      toast({
        title: "Verification Error",
        description: "An error occurred while verifying the utility bill.",
        variant: "destructive",
      });
    } finally {
      setIsVerifyingUb(false);
    }
  }, [projectId, documents, filePreviewUrls, toast]);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // Document state
    documents,
    filePreviewUrls,
    documentMetadata,
    fileLoading,
    uploadStatus,
    documentUpdateTimestamp,

    // Document counts (for lazy loading)
    documentCounts,
    countsLoading,
    loadedFields,

    // Drag state
    dragActive,

    // Gallery state
    galleryState,
    setGalleryState,

    // Verification state
    dlVerificationStatus,
    dlVerificationData,
    isVerifying,
    ubVerificationStatus,
    ubVerificationData,
    isVerifyingUb,

    // Document operations
    fetchProjectDocuments,
    fetchDocumentCounts,
    fetchFieldDocuments,
    handleFileChange,
    handleRemoveFile,

    // Drag handlers
    handleDrag,
    handleDrop,

    // Verification handlers
    handleVerifyDriversLicense,
    handleVerifyUtilityBill,

    // Gallery helpers
    buildGalleryDocuments,
    isImageFileType,

    // Direct setters
    setDocuments,
    setFilePreviewUrls,
    setDocumentMetadata,
    setDlVerificationStatus,
    setDlVerificationData,
    setUbVerificationStatus,
    setUbVerificationData,
  };
}
